<script setup lang="ts">
import Modal from '@/onenetwork/components/modal/Modal.vue'
import OneNetworkConfig from '@/onenetwork/core/onenetwork.config'
import { GoogleMap, Polygon, Marker, Polyline } from 'vue3-google-map'
import { computed, ref, watch } from 'vue'
import type { EstateProjectGeoJsonPolygon } from '@/modules/estate-project/types/estate-project-types'

interface SeedPoint {
  lat: number
  lng: number
}

interface MapMouseEventLike {
  latLng: {
    lat: () => number
    lng: () => number
  }
}

interface PolygonPoint {
  lat: number
  lng: number
}

interface Props {
  visible: boolean
  lat: number | null
  lng: number | null
  polygonGeojson: EstateProjectGeoJsonPolygon | null
  seedPoint?: SeedPoint | null
  mode?: 'modal' | 'inline'
  showProjectMarker?: boolean
  projectMarker?: SeedPoint | null
  editable?: boolean
  mapClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'modal',
  showProjectMarker: false,
  projectMarker: null,
  editable: true,
  mapClass: 'estate-project-popup-map',
})

const emit = defineEmits<{
  close: []
  confirm: [{ polygon_geojson: EstateProjectGeoJsonPolygon }]
  clear: []
  'project-marker-change': [{ lat: number; lng: number }]
}>()

const isInline = computed(() => props.mode === 'inline')
const isActive = computed(() => isInline.value || props.visible)

const center = computed(() => ({
  lat: Number(props.lat || props.seedPoint?.lat || 10.7749),
  lng: Number(props.lng || props.seedPoint?.lng || 106.7012),
}))

const points = ref<PolygonPoint[]>([])
const closed = ref(false)
const inlineMapRef = ref<InstanceType<typeof GoogleMap> | null>(null)
const modalMapRef = ref<InstanceType<typeof GoogleMap> | null>(null)

watch(
  [isActive, () => props.polygonGeojson, () => props.seedPoint],
  ([active]) => {
    if (!active) return

    const ring = props.polygonGeojson?.coordinates?.[0] || []
    if (ring.length > 0) {
      const cloned = ring.map((pair: number[]) => ({ lat: Number(pair[1]), lng: Number(pair[0]) }))
      points.value = cloned.slice(0, -1)
      closed.value = points.value.length >= 3
      return
    }

    if (props.seedPoint) {
      points.value = [{ lat: props.seedPoint.lat, lng: props.seedPoint.lng }]
      closed.value = false
      return
    }

    points.value = []
    closed.value = false
  },
  { immediate: true },
)

const canClosePolygon = computed(() => props.editable && !closed.value && points.value.length >= 3)

const buildGeoJson = (): EstateProjectGeoJsonPolygon => {
  const ring = points.value.map((item) => [item.lng, item.lat])
  ring.push([points.value[0].lng, points.value[0].lat])
  return {
    type: 'Polygon',
    coordinates: [ring],
  }
}

const confirmPolygon = (closeAfterConfirm = !isInline.value) => {
  if (points.value.length < 3) return
  emit('confirm', { polygon_geojson: buildGeoJson() })
  if (closeAfterConfirm) {
    emit('close')
  }
}

const clearPolygon = () => {
  points.value = []
  closed.value = false
  emit('clear')
}

const handleMapClick = (e: MapMouseEventLike) => {
  if (!props.editable || closed.value || !e?.latLng) return
  points.value = [
    ...points.value,
    {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    },
  ]
}

const handleDragPoint = (index: number, e: MapMouseEventLike) => {
  if (!props.editable || !e?.latLng) return
  points.value = points.value.map((point, pointIndex) => {
    if (pointIndex !== index) return point
    return {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    }
  })
  if (closed.value) {
    confirmPolygon(false)
  }
}

const handleClickPoint = (index: number) => {
  if (!props.editable || index !== 0 || !canClosePolygon.value) return
  closed.value = true
  confirmPolygon(!isInline.value)
}

const handleProjectMarkerDrag = (e: MapMouseEventLike) => {
  if (!props.editable || !e?.latLng) return
  emit('project-marker-change', {
    lat: e.latLng.lat(),
    lng: e.latLng.lng(),
  })
}

const polylinePaths = computed(() => {
  if (points.value.length === 0) return points.value
  if (!closed.value) return points.value
  return [...points.value, points.value[0]]
})

const polygonPaths = computed(() => {
  if (!closed.value || points.value.length < 3) return []
  return [...points.value, points.value[0]]
})

const CIRCLE_ICON_PATH = 'M 0,0 m -1,0 a 1,1 0 1,0 2,0 a 1,1 0 1,0 -2,0'

const pointMarkerOptions = (point: PolygonPoint, index: number) => ({
  position: point,
  draggable: props.editable,
  icon: {
    path: CIRCLE_ICON_PATH,
    fillColor: '#00B2FF',
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: 2,
    scale: index === 0 ? 7 : 6,
  },
})

const projectMarkerOptions = computed(() => {
  if (!props.projectMarker) return null
  return {
    position: props.projectMarker,
    draggable: props.editable,
    zIndex: 999,
  }
})

watch(
  () => inlineMapRef.value?.ready,
  (ready) => {
    if (!ready || !inlineMapRef.value?.map) return
    inlineMapRef.value.map.setMapTypeId('satellite')
  },
)

watch(
  () => modalMapRef.value?.ready,
  (ready) => {
    if (!ready || !modalMapRef.value?.map) return
    modalMapRef.value.map.setMapTypeId('satellite')
  },
)
</script>

<template>
  <div v-if="isInline" class="estate-project-popup-map-wrap">
    <GoogleMap
      ref="inlineMapRef"
      :api-key="OneNetworkConfig.map.apiKey"
      :center="center"
      :zoom="17"
      :options="{
        mapTypeId: 'satellite',
        disableDefaultUI: true,
        clickableIcons: false,
        mapTypeControl: false,
        zoomControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      }"
      :class="['estate-project-popup-map', mapClass]"
      @click="handleMapClick"
    >
      <Polyline
        v-if="polylinePaths.length > 1"
        :options="{
          path: polylinePaths,
          strokeColor: '#00B2FF',
          strokeOpacity: 1,
          strokeWeight: 2,
        }"
      />
      <Polygon
        v-if="polygonPaths.length > 0"
        :options="{
          paths: polygonPaths,
          strokeColor: '#00B2FF',
          strokeOpacity: 1,
          strokeWeight: 2,
          fillColor: '#006FFF',
          fillOpacity: 0.45,
        }"
      />
      <Marker
        v-if="showProjectMarker && projectMarkerOptions"
        :options="projectMarkerOptions"
        @dragend="(e: MapMouseEventLike) => handleProjectMarkerDrag(e)"
      />
      <Marker
        v-for="(point, index) in points"
        :key="`point-${index}`"
        :options="pointMarkerOptions(point, index)"
        @dragend="(e: MapMouseEventLike) => handleDragPoint(index, e)"
        @click="() => handleClickPoint(index)"
      />
    </GoogleMap>
    <div class="estate-project-popup-actions" v-if="editable">
      <button type="button" class="estate-project-popup-btn" @click="clearPolygon">Xóa ranh giới</button>
    </div>
    <div class="estate-project-popup-hint">
      {{ canClosePolygon ? 'Bấm vào điểm đầu tiên để chốt ranh giới' : 'Bấm lên bản đồ để thêm các điểm ranh giới' }}
    </div>
  </div>

  <Modal v-else :show="visible" title="Vẽ polygon dự án" @on:close="emit('close')">
    <div class="estate-project-popup-map-wrap">
      <GoogleMap
        ref="modalMapRef"
        :api-key="OneNetworkConfig.map.apiKey"
        :center="center"
        :zoom="17"
        :options="{
          mapTypeId: 'satellite',
          disableDefaultUI: true,
          clickableIcons: false,
          mapTypeControl: false,
          zoomControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        }"
        class="estate-project-popup-map"
        @click="handleMapClick"
      >
        <Polyline
          v-if="polylinePaths.length > 1"
          :options="{
            path: polylinePaths,
            strokeColor: '#00B2FF',
            strokeOpacity: 1,
            strokeWeight: 2,
          }"
        />
        <Polygon
          v-if="polygonPaths.length > 0"
          :options="{
            paths: polygonPaths,
            strokeColor: '#00B2FF',
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: '#006FFF',
            fillOpacity: 0.45,
          }"
        />
        <Marker
          v-for="(point, index) in points"
          :key="`point-${index}`"
          :options="pointMarkerOptions(point, index)"
          @dragend="(e: MapMouseEventLike) => handleDragPoint(index, e)"
          @click="() => handleClickPoint(index)"
        />
      </GoogleMap>
      <div class="estate-project-popup-hint">
        {{ canClosePolygon ? 'Bấm vào điểm đầu tiên để chốt ranh giới' : 'Bấm lên bản đồ để thêm các điểm ranh giới' }}
      </div>
    </div>
  </Modal>
</template>

<style scoped>
.estate-project-popup-map-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.estate-project-popup-map {
  width: 100%;
  height: 420px;
  border-radius: 8px;
  overflow: hidden;
}

.estate-project-popup-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.estate-project-popup-btn {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #111827;
  padding: 6px 12px;
  font-size: 12px;
}

.estate-project-popup-btn.primary {
  border-color: #0059ff;
  background: #0059ff;
  color: #fff;
}

.estate-project-popup-btn:disabled {
  opacity: 0.5;
}

.estate-project-popup-hint {
  font-size: 12px;
  color: #4b5563;
}
</style>
